import { useEffect, useState } from 'react'
import type { Bet } from 'src/types/domain/Bet'
import type { BetDetails } from 'src/types/domain/BetDetails'
import type { MarbleGame } from 'src/types/domain/Game'
import type { NextRaceBet } from 'src/types/domain/NextRaceBet'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import ClockIcon from 'src/app/assets/icons/ico-clock.svg'
import { roundAmount } from 'src/utils/roundAmount'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { displayMoney } from 'src/utils/displayAmount'
import { useGetPlayerChannel } from 'src/store/socket'
import { useGetCurrentPlayer } from 'src/store/player'
import { usePayoutCalculation } from 'src/hooks/usePayoutCalculation'
import { Button } from '../Button'
import CurrentBetPane from '../CurrentBetPanel/components/CurrentBetPane'

type Props = Readonly<{
  game: MarbleGame
  nextRaceBet?: NextRaceBet
  onEdit: () => void
  onCancel: () => void
  setPlacingNextRaceBet: (value: boolean) => void
  isRaceReady?: boolean
}>

export function NextRaceBetPanel({
  game,
  nextRaceBet,
  onEdit,
  onCancel,
  setPlacingNextRaceBet,
  isRaceReady
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const currentPlayer = useGetCurrentPlayer()
  const playerChannel = useGetPlayerChannel()
  const [potentialWin, setPotentialWin] = useState<string>('0.00')

  const calculatePayout = usePayoutCalculation({ playerChannel, player: currentPlayer, game })

  useEffect(() => {
    if (!nextRaceBet) return

    const updatePayout = async () => {
      const payoutResult = await calculatePayout({
        betAmount: nextRaceBet.betAmount,
        selectedMarbles: nextRaceBet.selectedMarbles,
        betType: nextRaceBet.betType,
        inventoryItemIds: nextRaceBet.selectedInventoryItemIds
      })

      if (payoutResult) {
        setPotentialWin(payoutResult.payout.toString())
      }
    }

    void updatePayout()
  }, [
    nextRaceBet?.betAmount,
    nextRaceBet?.selectedMarbles,
    nextRaceBet?.betType,
    nextRaceBet?.selectedInventoryItemIds
  ])

  if (!nextRaceBet) return null

  const currentRace = game.races[0]
  const selectedItems = nextRaceBet.selectedMarbles.map((m) => m.uuid)

  const bet: Bet = {
    id: 'next-race',
    playerId: '',
    raceId: currentRace.uuid,
    raceConfigurationId: currentRace.raceConfigurationId,
    betAmount: parseFloat(nextRaceBet.betAmount),
    currency: nextRaceBet.currency,
    betDetails: {
      type: nextRaceBet.betType,
      selectedItems: selectedItems.slice(
        0,
        nextRaceBet.betType === 'PickWinner'
          ? undefined
          : nextRaceBet.betType === 'Tricast' || nextRaceBet.betType === 'CombinationTricast'
            ? 3
            : 2
      ),
      ...(nextRaceBet.selectedInventoryItemIds?.length
        ? { inventoryItemIds: nextRaceBet.selectedInventoryItemIds }
        : {})
    } as BetDetails,
    status: 'pending',
    potentialPayout: roundAmount(potentialWin),
    createdAt: new Date().toISOString()
  }

  // Crash-game (crash): unified single-card layout
  if (game.track.gameType === 'crash') {
    const currencySymbol = getCurrencySymbol(nextRaceBet.currency)

    return (
      <div className="crash-bet-container">
        <div className="crash-bet-pane crash-bet-pane--next crash-bet-pane--column">
          <div className="crash-bet-header">
            <div className="crash-bet-header__label">
              {isRaceReady ? (
                <>
                  <ClockIcon />
                  <span>{t('Placing bet...')}</span>
                </>
              ) : (
                <>
                  <FlagCheckeredIcon />
                  <span>{t('Bet placed on next game')}</span>
                </>
              )}
            </div>
            <div className="crash-bet-header__actions">
              {isRaceReady ? (
                <Button className="btn btn-cancel" onClick={onCancel} type="button">
                  {t('Cancel')}
                </Button>
              ) : (
                <>
                  <Button
                    className="btn btn-edit"
                    onClick={() => {
                      onEdit()
                      setPlacingNextRaceBet(true)
                    }}
                    type="button"
                  >
                    {t('Edit')}
                  </Button>
                  <Button className="btn btn-cancel" onClick={onCancel} type="button">
                    {t('Cancel')}
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="crash-bet-stake-row">
            <span className="crash-bet-info__value">
              {currencySymbol}
              {displayMoney(nextRaceBet.betAmount, true)}
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="next-race-bet-panel">
      <div className="next-race-bet-panel__header">
        <div className="next-race-bet-label">
          {isRaceReady ? (
            <>
              <ClockIcon />
              <span>{t('Placing bet...')}</span>
            </>
          ) : (
            <>
              <FlagCheckeredIcon />
              <span>{t('Bet placed on next game')}</span>
            </>
          )}
        </div>
        <div className="next-race-bet-actions">
          {isRaceReady ? (
            <Button className="btn btn-cancel" onClick={onCancel} type="button">
              {t('Cancel')}
            </Button>
          ) : (
            <>
              <Button
                className="btn btn-edit"
                onClick={() => {
                  onEdit()
                  setPlacingNextRaceBet(true)
                }}
                type="button"
              >
                {t('Edit')}
              </Button>
              <Button className="btn btn-cancel" onClick={onCancel} type="button">
                {t('Cancel')}
              </Button>
            </>
          )}
        </div>
      </div>
      <CurrentBetPane currentBet={bet} game={game} isNextRaceBet />
    </div>
  )
}
